import React, { useEffect } from "react";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import CleaningZone from "../../../../interfaces/robot/rooms/cleaning-zone";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import { selectTeachAndRepeatPathByZoneId } from "../../../../store/room/zone-planner/zone-planner-selectors";
import { selectNavLocalPlannerData } from "../../../../store/robot-configurations/robot-configurations-selectors";
import HttpFetchStatus from "../../../../enums/api/http-fetch-status";
import { selectDistanceUnit } from "../../../../store/app-configurations/app-configurations-selectors";
import useDistanceUnitValue from "../../../../hooks/useDistanceUnitValue";
import { DistanceValueUnit, DistanceUnit } from "../../../../enums/configuration/units";
import {
  useUpdateTeachAndRepeatParameters,
  AllowedDeviationSlider,
} from "../../Teaching/TeachAndRepeatParameterSettings";
import { convertValueToCorrectMetric } from "../../../../utils/util_functions";
import ReadOnlyProtectedForm from "../../../Inputs/ReadOnlyProtectedForm";
import OptionSwitchInput from "../../../Inputs/OptionSwitchInput";
import {
  CleaningConfigurationLevel,
  getCleaningModeOptionText,
} from "../../../../interfaces/robot/cleaning-configurations/global-cleaning-configuration";
import { CustomTuneFiltersIcon, InheritIcon } from "../../../../utils/icons/icons";
import {
  getTeachAndRepeatResolveTypeList,
  TeachAndRepeatResolveType,
} from "../../../../enums/robot/rooms/teach-and-repeat-resolve-type";

const TeachAndRepeatSettingsForm: React.FC<{ zone: CleaningZone }> = ({ zone }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();
  const { getNavLocalPlanner } = useSharedActions();

  const teachAndRepeatZonePath = useSharedSelector((state) => selectTeachAndRepeatPathByZoneId(state, zone.id));
  const localPlannerData = useSharedSelector((state) => selectNavLocalPlannerData(state));

  const distanceUnit = useSharedSelector((state) => selectDistanceUnit(state));
  const { multiplier } = useDistanceUnitValue(0, DistanceValueUnit.METERS);

  const updateTeachAndRepeatParameters = useUpdateTeachAndRepeatParameters();
  useEffect(() => {
    if (localPlannerData.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getNavLocalPlanner());
    }
  }, [dispatch, localPlannerData.rest.getStatus]);

  if (
    teachAndRepeatZonePath != null &&
    localPlannerData.data != null &&
    localPlannerData.data?.virtualCorridorParams.allowedDeviation != null
  ) {
    const inheritedResolveType = localPlannerData.data?.virtualCorridorParams.resolveConfiguration;
    const currentResolveConfig = teachAndRepeatZonePath.resolveConfiguration;
    const inheritedPathDeviation = localPlannerData.data?.virtualCorridorParams.allowedDeviation;
    const untouchedPathDeviation = `${t("inherited-from-global")} (${convertValueToCorrectMetric(inheritedPathDeviation, distanceUnit, multiplier)} ${distanceUnit === DistanceUnit.METRIC ? t("meter-unit") : t("label-feet")})`;
    const currentPathDeviation = teachAndRepeatZonePath.allowedDeviation;
    const deviationValue = currentPathDeviation || inheritedPathDeviation;

    return (
      <ReadOnlyProtectedForm>
        <Stack spacing={2} maxWidth="100%">
          <OptionSwitchInput
            label={t("resolveConfiguration")}
            options={getTeachAndRepeatResolveTypeList.map((rt) => ({
              label: t(rt),
              value: rt,
              helpText: t(`${rt}-tooltip`)
            }))}
            value={currentResolveConfig}
            onChange={(v: string) =>
              updateTeachAndRepeatParameters({
                trZone: {
                  zoneId: teachAndRepeatZonePath.zoneId,
                  resolveConfiguration: v as TeachAndRepeatResolveType,
                },
              })
            }
            subButton={{
              label:
                currentResolveConfig == null
                  ? t("button-override-default-configs")
                  : getCleaningModeOptionText(
                      null,
                      CleaningConfigurationLevel.Zone,
                      undefined,
                      inheritedResolveType ?? undefined,
                    ),
              startIcon: currentResolveConfig == null ? <CustomTuneFiltersIcon /> : <InheritIcon />,
              onClick: () =>
                updateTeachAndRepeatParameters({
                  trZone: {
                    zoneId: teachAndRepeatZonePath.zoneId,
                    resolveConfiguration: currentResolveConfig == null ? inheritedResolveType : null,
                  },
                }),
            }}
            buttonOverrideText={
              currentResolveConfig == null
                ? getCleaningModeOptionText(null, CleaningConfigurationLevel.Zone, undefined, inheritedResolveType)
                : undefined
            }
          />

          <Stack spacing={0.5}>
            <InputLabel>
              {distanceUnit === DistanceUnit.METRIC
                ? t("navigator-settings-path-deviation-metric")
                : t("navigator-settings-path-deviation-imperial")}
            </InputLabel>
            {currentPathDeviation == null ? (
              <Typography>{untouchedPathDeviation}</Typography>
            ) : (
              <AllowedDeviationSlider
                zoneId={teachAndRepeatZonePath.zoneId}
                value={deviationValue}
                distanceUnit={distanceUnit}
                multiplier={multiplier}
              />
            )}
            <Button
              onClick={() =>
                updateTeachAndRepeatParameters({
                  trZone: {
                    zoneId: teachAndRepeatZonePath.zoneId,
                    allowedDeviation: currentPathDeviation == null ? inheritedPathDeviation : null,
                  },
                })
              }
              variant="text"
              size="small"
              startIcon={currentPathDeviation == null ? <CustomTuneFiltersIcon /> : <InheritIcon />}
              sx={{ width: "max-content", maxWidth: "100%" }}
            >
              {currentPathDeviation == null ? t("button-override-default-configs") : untouchedPathDeviation}
            </Button>
          </Stack>
        </Stack>
      </ReadOnlyProtectedForm>
    );
  }
  return null;
};

export default TeachAndRepeatSettingsForm;
